/**
 * State Analyser — National role screen
 * FR-N08 to FR-N12
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight, Download } from 'lucide-react';
import { statesData, gujaratDistricts, nationalKPIs } from '../../data/mockData';

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-semibold' :
  pr >= 65 ? 'text-amber-600 font-semibold' :
             'text-red-600 font-semibold';

// ── Tile definitions ──────────────────────────────────────────────────────────

interface TileDef {
  label: string;
  textCls: string;
  bgCls: string;
  ringCls: string;
  filterFn: (s: typeof statesData[0]) => boolean;
}

const TILES: TileDef[] = [
  {
    label: 'PR below 70%',
    textCls: 'text-red-600', bgCls: 'bg-red-50', ringCls: 'ring-red-500',
    filterFn: s => s.prPercent < 70,
  },
  {
    label: 'Generation dropping MoM',
    textCls: 'text-amber-600', bgCls: 'bg-amber-50', ringCls: 'ring-amber-500',
    filterFn: s => s.momTrend < 0,
  },
  {
    label: 'Low availability',
    textCls: 'text-green-700', bgCls: 'bg-green-50', ringCls: 'ring-green-500',
    filterFn: s => s.activePercent < 80,
  },
  {
    label: 'Low specific yield',
    textCls: 'text-teal-600', bgCls: 'bg-teal-50', ringCls: 'ring-teal-500',
    filterFn: s => (s.generationGWh / s.capacityGWp) < nationalKPIs.avgSpecificYield,
  },
  {
    label: 'High alarm rate',
    textCls: 'text-purple-600', bgCls: 'bg-purple-50', ringCls: 'ring-purple-500',
    filterFn: s => s.alarms / s.plants > 0.5,
  },
  {
    label: 'High offline rate',
    textCls: 'text-orange-600', bgCls: 'bg-orange-50', ringCls: 'ring-orange-500',
    filterFn: s => (1 - s.activePercent / 100) > 0.05,
  },
];

// Tile counts computed once from all states
const tileCounts = TILES.map(t => statesData.filter(t.filterFn).length);

// ── District columns use same type shape ────────────────────────────────────

export default function NationalStates() {
  const [activeTile, setActiveTile]   = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);

  // Table: national (states) or state (districts)
  const scope = selectedState ? 'district' : 'national';

  // Filter national states by active tile
  const visibleStates = useMemo(() => {
    if (activeTile === null) return statesData;
    return statesData.filter(TILES[activeTile].filterFn);
  }, [activeTile]);

  const stateObj = selectedState
    ? statesData.find(s => s.name === selectedState)
    : null;

  // Export CSV
  const exportCSV = () => {
    const rows = scope === 'national'
      ? ['State,Plants,GWp,GWh Today,PR%,Avail%,CUF%,Alarms',
         ...visibleStates.map(s =>
           `${s.name},${s.plants},${s.capacityGWp.toFixed(2)},${s.generationGWh.toFixed(2)},${s.prPercent},${s.activePercent},${s.cufPercent},${s.alarms}`)]
      : ['District,Plants,GWp,GWh Today,PR%,Alarms',
         ...gujaratDistricts.map(d =>
           `${d.name},${d.plants},—,${d.generationGWh.toFixed(2)},${d.prPercent},—`)];

    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'state-analyser.csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-5">

      {/* ── Triage tiles (6 in one row) ── */}
      <div className="grid grid-cols-6 gap-3">
        {TILES.map((t, i) => {
          const isActive = activeTile === i;
          return (
            <div
              key={t.label}
              onClick={() => { setActiveTile(prev => prev === i ? null : i); }}
              className={`rounded-card border p-4 cursor-pointer transition-all hover:shadow-md ${t.bgCls}
                ${isActive ? `ring-2 ${t.ringCls} border-transparent` : 'border-border shadow-card'}`}
            >
              <p className="text-xs text-text-secondary font-semibold leading-tight">{t.label}</p>
              <p className={`text-2xl font-bold mt-1 ${t.textCls}`}>{tileCounts[i]}</p>
              <p className="text-xs text-text-tertiary mt-1">states</p>
            </div>
          );
        })}
      </div>

      {/* ── Filter bar ── */}
      <div className="flex items-center gap-3">
        <select
          value={selectedState ?? ''}
          onChange={e => { setSelectedState(e.target.value || null); setActiveTile(null); }}
          className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
        >
          <option value="">All States</option>
          {statesData.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        {activeTile !== null && (
          <button
            onClick={() => setActiveTile(null)}
            className="text-xs text-accent hover:underline px-2 py-1 border border-accent/30 rounded-input"
          >
            Clear filter
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={exportCSV}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* ── Breadcrumb (when drilling down) ── */}
      {scope === 'district' && (
        <div className="flex items-center gap-1 text-xs text-text-secondary">
          <button onClick={() => setSelectedState(null)} className="text-accent hover:underline font-medium">National</button>
          <ChevronRight size={11} className="text-text-tertiary" />
          <span className="font-medium text-text-primary">{selectedState}</span>
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="text-xs font-semibold text-text-primary">
            {scope === 'national'
              ? `${visibleStates.length} state${visibleStates.length !== 1 ? 's' : ''}${activeTile !== null ? ` · ${TILES[activeTile].label}` : ''}`
              : `Districts — ${selectedState}`
            }
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Name</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Capacity</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Gen today</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Sp. yield</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">PR %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Avail %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">CUF %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">MoM %</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Active / Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Alarms</th>
              </tr>
            </thead>
            <tbody>
              {scope === 'national' ? (
                visibleStates.map(s => {
                  const activePl  = Math.round(s.plants * s.activePercent / 100);
                  const offlinePl = s.plants - activePl;
                  const spYield   = (s.generationGWh / s.capacityGWp).toFixed(2);
                  return (
                    <tr key={s.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                      <td className="py-2.5 px-4">
                        <button
                          onClick={() => setSelectedState(s.name)}
                          className="font-medium text-accent hover:underline text-left"
                        >
                          {s.name}
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{s.plants.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{s.capacityGWp.toFixed(2)} GWp</td>
                      <td className="py-2.5 px-3 text-right font-medium text-text-primary">{s.generationGWh.toFixed(2)} GWh</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{spYield} kWh/kWp</td>
                      <td className={`py-2.5 px-3 text-right ${prCls(s.prPercent)}`}>{s.prPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right ${s.activePercent < 80 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{s.activePercent.toFixed(0)}%</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{s.cufPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right font-semibold ${s.momTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {s.momTrend >= 0 ? '↑' : '↓'}{Math.abs(s.momTrend).toFixed(1)}%
                      </td>
                      <td className="py-2.5 px-3 text-xs text-text-secondary">
                        {activePl.toLocaleString()} active · <span className={offlinePl > s.plants * 0.05 ? 'text-red-600 font-semibold' : ''}>{offlinePl.toLocaleString()} offline</span>
                      </td>
                      <td className={`py-2.5 px-3 text-right ${s.alarms > 100 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{s.alarms}</td>
                    </tr>
                  );
                })
              ) : (
                // District view (using gujaratDistricts as proxy for all states)
                gujaratDistricts.map(d => {
                  const cap = (d.plants * 3 / 1000).toFixed(2); // Approx 3kWp avg
                  const spYield = (d.generationGWh * 1000 / (d.plants * 3)).toFixed(2);
                  const activePl = Math.round(d.plants * (d.status === 'online' ? 0.91 : 0.82));
                  return (
                    <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                      <td className="py-2.5 px-4 font-medium text-text-primary">{d.name}</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{cap} GWp</td>
                      <td className="py-2.5 px-3 text-right font-medium text-text-primary">{d.generationGWh.toFixed(2)} GWh</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{spYield} kWh/kWp</td>
                      <td className={`py-2.5 px-3 text-right ${prCls(d.prPercent)}`}>{d.prPercent.toFixed(1)}%</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.status === 'online' ? '91' : '82'}%</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{(d.prPercent * 0.22).toFixed(1)}%</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">—</td>
                      <td className="py-2.5 px-3 text-xs text-text-secondary">
                        {activePl.toLocaleString()} active · {(d.plants - activePl).toLocaleString()} offline
                      </td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">—</td>
                    </tr>
                  );
                })
              )}
              {scope === 'national' && visibleStates.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-10 text-center text-xs text-text-tertiary">
                    No states match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
